import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { User } from '@supabase/supabase-js'
import toast from 'react-hot-toast'
import { usersAPI } from '../lib/api'

interface AuthContextType {
  user: User | null
  profile: any | null
  sellerId: string | null
  franchiseName: string | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<any | null>(null)
  const [sellerId, setSellerId] = useState<string | null>(null)
  const [franchiseName, setFranchiseName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check active sessions
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
        // Store the access token for API calls
        if (session.access_token) {
          localStorage.setItem('auth_token', session.access_token)
          // Fetch seller info after setting token
          fetchSellerInfo()
        }
      }
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        fetchProfile(session.user.id)
        // Store the access token for API calls
        if (session?.access_token) {
          localStorage.setItem('auth_token', session.access_token)
          // Fetch seller info after setting token
          fetchSellerInfo()
        }
      } else {
        setProfile(null)
        setSellerId(null)
        setFranchiseName(null)
        // Clear the token on logout
        localStorage.removeItem('auth_token')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchSellerInfo = async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setSellerId(data.seller_id)
        setFranchiseName(data.franchise_name)
      }
    } catch (error) {
      console.error('Error fetching seller info:', error)
      // Set to null on error - demo mode
      setSellerId(null)
      setFranchiseName(null)
    }
  }

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) throw error
      setProfile(data)
      
      // Check if this is the super user and initialize if needed
      if (data?.email === 'marco.heer@pflegehilfe-senioren.de' && data?.role !== 'admin') {
        try {
          await usersAPI.initSuperUser()
          // Refetch profile to get updated role
          const { data: updatedProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single()
          if (updatedProfile) {
            setProfile(updatedProfile)
          }
        } catch (initError) {
          console.error('Error initializing super user:', initError)
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
    }
  }

  const signInWithGoogle = async () => {
    try {
      // Store current path to redirect back after auth
      const currentPath = window.location.pathname
      if (currentPath !== '/login') {
        sessionStorage.setItem('redirectAfterAuth', currentPath)
      }
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            hd: 'pflegehilfe-senioren.de', // Domain restriction hint
            prompt: 'select_account' // Force account selection
          },
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) throw error
    } catch (error: any) {
      toast.error(error.message || 'Anmeldung fehlgeschlagen')
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      // Clear the token
      localStorage.removeItem('auth_token')
      toast.success('Erfolgreich abgemeldet')
    } catch (error: any) {
      toast.error(error.message || 'Abmeldung fehlgeschlagen')
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      sellerId,
      franchiseName,
      loading,
      signInWithGoogle,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}